// @ts-strict-ignore
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash/debounce';

import type { Action, WorkflowOperationMessage, ThresholdResults } from 'flow/store/engine/types';
import { OperationMessageType } from 'flow/store/engine/types';
import { ActionType, WorkflowOperationType } from 'flow/store/engine/constants';
import FocusedActionText from 'flow/editor/components/thresholds/FocusedActionText';
import { generateSemanticLines } from 'flow/editor/components/thresholds/helpers';
import { getMLAsUsedInRules } from 'flow/service/mla';
import {
  isActionAnImportedValue,
  isActionOfType,
  isMidPrecedenceAction,
} from 'flow/service/actions';
import { MLAEditorContext } from 'flow/editor/components/mla/context';
import type { MLASourceData } from 'flow/editor/components/mla/types';
import MLAFootnotesContainer from 'flow/editor/components/mla/MLAFootnotesContainer/MLAFootnotesContainer';
import {
  createWorkflowOperationMessage,
  dismissWorkflowOperationMessage,
  resetNodeAndConnectorState,
  setConnectorCanCreateItemStatus,
} from 'flow/store/redux/slice';
import { RuleLinesContainer } from 'flow/editor/components/thresholds/RuleLinesContainer';
import {
  selectFocusedAction,
  selectLegacyMappings,
  selectOutcomeActions,
  selectPreviewAction,
  selectWorkflowData,
  selectWorkflowOperationDescs,
  selectWorkflowRoutingActions,
} from 'flow/store/redux/selectors';
import { StyledPre } from 'flow/editor/styles';
import { useCaretControl } from 'flow/editor/components/useCaretControl/useCaretControl';
import { useKeyboardKeyEvent } from 'flow/editor/components/WorkflowCanvas/hooks';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { ContainerType } from './types';
import {
  StyledRuleLineContentWrapper,
  StyledRuleThresholdBottomArea,
  StyledRuleThresholdContainer,
  StyledRuleThresholdContentArea,
  StyledRuleThresholdEditor,
  StyledThresholdTabContainer,
} from './styles';
import { ImportedAttributeDetails } from '../ImportedAttributeDetails';
import type { IsolatedEditingBlockData, RuleItemRef, RuleItemRefs } from '../types';
import MatrixModel, { ReadOnlyMatrixModel } from '../MatrixModel';
import { UserContext } from '~/dashboard/context';
import { RuleEditorContext } from '../context';
import AssistanceToolbar from '../../AssistanceToolbar';
import { checkIfRuleHasAmbiguousKey } from '~/dashboard/views/workflows/service/thresholds';
import { CaretIndexType } from '../../useCaretControl/constants';
import { findLastOperation } from 'flow/service/assistance';
import type { NodeTooltipProps } from '../../Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';
import RuleBuilderContainer from './RuleBuilderContainer/RuleBuilderContainer';
import { useEditorAccess } from 'flow/editor/context';
import TagDescription from './TagDescription';
import { checkIfEditorModeIsEditable } from 'flow/service/general';
import EvidencesTable from '~/entities/evaluations/TagRuleModal/EvidencesTable';
import { reversedObjectKeyVal } from '~/entities/evaluations/TagRuleModal/utils';
import {
  selectDimensions,
  selectEntityToken,
  selectEvaluationToken,
  selectIsRuleExplainabilityPortfolioEvaluation,
  selectSelectedEvidenceThresholdId,
  selectThresholdOutputResults,
} from 'flow/store/redux/selectors/workflowFeatures';
import { useSourceActionThresholdResults } from './utils';

type ThresholdsTabProps = {
  action?: Action;
  containerType?: ContainerType;
  editable?: boolean;
  isPreviewAction?: boolean;
  // if not specified, do not render rule numbers
  ruleNumberConfig?: {
    // enables drag and drop for the editor - rule numbers are the drag trigger
    isDragEnabled: boolean;
  };
  simpleEditorMode?: boolean;
  thresholdResults?: ThresholdResults;
  selectedEvidenceThresholdId?: number | null;
  ActionHoverTooltip?: (NodeTooltipProps: NodeTooltipProps) => JSX.Element;
};

export default function ThresholdsTab({
  action,
  containerType = ContainerType.DRAWER,
  editable = true,
  isPreviewAction = false,
  ruleNumberConfig,
  simpleEditorMode = false,
  thresholdResults = null,
  selectedEvidenceThresholdId = null,
  ActionHoverTooltip = null,
}: ThresholdsTabProps) {
  const dispatch = useAppDispatch();
  const workflowData = useAppSelector(selectWorkflowData);
  const operationDescriptions = useAppSelector(selectWorkflowOperationDescs);
  const focusedAction = useAppSelector(selectFocusedAction);
  const outcomeActions = useAppSelector(selectOutcomeActions);
  const workflowRoutingActions = useAppSelector(selectWorkflowRoutingActions);
  const previewActionBeingBuilt = useAppSelector(selectPreviewAction);
  const thresholdOutputResults = useAppSelector(selectThresholdOutputResults);
  const isREPortfolioEvaluation = useAppSelector(selectIsRuleExplainabilityPortfolioEvaluation);
  const legacyMappings = useAppSelector(selectLegacyMappings);
  const dimensions = useAppSelector(selectDimensions);
  const evaluationToken = useAppSelector(selectEvaluationToken);
  const entityToken = useAppSelector(selectEntityToken);
  const thresholdId = useAppSelector(selectSelectedEvidenceThresholdId);

  const reversedActionMappings = useMemo(() => (
    reversedObjectKeyVal(legacyMappings?.actions || {})
  ), [legacyMappings?.actions]);

  const { editorMode } = useEditorAccess();
  const inEditMode = checkIfEditorModeIsEditable(editorMode);

  const { featureFlags } = useContext(UserContext);
  const { thresholds } = workflowData;

  const sourceAction = action || focusedAction;

  const legacyActionId = useMemo(() => (
    reversedActionMappings?.[sourceAction?.publicId] ?? null
  ), [reversedActionMappings, sourceAction]);

  const sourceActionThresholdResults = useSourceActionThresholdResults(
    thresholdResults,
    thresholdOutputResults,
    legacyActionId,
    isREPortfolioEvaluation,
  );

  const focusedActionMatrixModel = useMemo<JSX.Element | null>(
    () => {
      if (!sourceAction || sourceAction.type !== ActionType.MATRIX_MODEL) {
        return null;
      }
      return (!editable)
        ? <ReadOnlyMatrixModel matrixModel={sourceAction.matrixModel} />
        : <MatrixModel action={sourceAction} />;
    },
    [editable, sourceAction],
  );

  const importedAttributeDetails = useMemo<JSX.Element | null>(
    () => (
      sourceAction
      && isActionAnImportedValue(sourceAction)
      && <ImportedAttributeDetails action={sourceAction} />
    ) || null,
    [sourceAction],
  );

  const tagDescription = useMemo(() => {
    if (sourceAction.type !== ActionType.TAG || !(sourceAction.tag?.description)) {
      return null;
    }

    return <TagDescription />;
  }, [sourceAction]);

  const ruleEditorPanelBoxContainerRef = useRef(null);
  const [
    ruleEditorPanelBoxContainerWidth,
    setRuleEditorPanelBoxContainerWidth,
  ] = useState<number>(0);

  const [ruleItemRefs, setRuleItemRefs] = useState<RuleItemRefs>({});

  const updateRuleItemRefs = useCallback((refItem: RuleItemRef) => {
    if (ruleItemRefs[refItem.path]?.width !== refItem.width) {
      setRuleItemRefs(prevState => ({
        ...prevState,
        [refItem.path]: {
          width: refItem.width,
        },
      }));
    }
  }, [ruleItemRefs]);

  const [isolatedEditingBlock, setIsolatedEditingBlock] = useState<IsolatedEditingBlockData>({
    originPublicId: '',
    originType: undefined,
    originCaretIndex: undefined,
  });

  const [isMatrixModelFocused, setIsMatrixModelFocused] = useState<boolean>(false);

  // Track initialization state to prevent passing stale rulesData to useCaretControl
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  const dispatchAssistanceToolbar = useCallback((payload: WorkflowOperationMessage) => {
    dispatch(createWorkflowOperationMessage({ object: payload }));
  }, [dispatch]);

  const dismissAssistanceToolbar = useCallback((payload: Partial<WorkflowOperationMessage>) => {
    const messageToDismiss = findLastOperation({
      operations: operationDescriptions,
      publicId: payload.publicId,
      messages: [payload.message],
    });
    if (messageToDismiss) {
      dispatch(dismissWorkflowOperationMessage({ id: messageToDismiss.id }));
    }
  }, [dispatch, operationDescriptions]);

  const caretControl = useCaretControl({
    rulesData: isInitializing ? undefined : sourceAction?.rules,
    sourceId: sourceAction?.publicId,
    caretType: CaretIndexType.RULES,
    isPreviewAction: sourceAction?.type === 'preview',
    ruleEditorPanelBoxContainerRef,
    mlaEditorEnabled: Boolean(isolatedEditingBlock.originType),
    isMatrixModelFocused,
    supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
    dispatchAssistanceToolbar,
    dismissAssistanceToolbar,
  });

  const semanticLineBreakData = useMemo(() => {
    if (caretControl?.ruleEditorFlatCaretIndices?.length) {
      return generateSemanticLines({
        flatCaretIndices: caretControl.ruleEditorFlatCaretIndices,
        ruleItemRefs,
        ruleEditorPanelBoxContainerWidth,
      });
    }

    return {
      groupedByLines: [],
      lineBreakPointIndices: [],
    };
  }, [caretControl?.ruleEditorFlatCaretIndices, ruleItemRefs, ruleEditorPanelBoxContainerWidth]);

  const mlaUsedInRules = useMemo(() => (isInitializing ? [] : getMLAsUsedInRules({
    sourceAction,
    thresholds,
  })), [sourceAction, thresholds, isInitializing]);

  const showElseClause = useMemo(() => ((
    isActionOfType(action, ActionType.OUTCOME)
    && isMidPrecedenceAction(action, outcomeActions)
  ) || (
    isActionOfType(action, ActionType.WORKFLOW)
      && isMidPrecedenceAction(action, workflowRoutingActions))
  ), [action, outcomeActions, workflowRoutingActions]);

  const [
    isDragItemHoveringOnDropZone,
    setIsDragItemHoveringOnDropZone,
  ] = useState<number>(undefined);

  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [
    containsAmbiguousRuleOperator, setContainsAmbiguousRuleOperator,
  ] = useState<boolean>(false);

  const context = React.useMemo(() => ({
    ruleEditorPanelBoxContainerWidth,
    ruleItemRefs,
    updateRuleItemRefs,
    semanticLineBreakData,
    sourceAction,
    isPreviewAction,
    mlaUsedInRules,
    showElseClause,
    renderRuleNumbers: Boolean(ruleNumberConfig),
    isDragEnabled: ruleNumberConfig?.isDragEnabled,
    isDragItemHoveringOnDropZone,
    setIsDragItemHoveringOnDropZone,
    isDragging,
    setIsDragging,
    simpleEditorMode,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    containsAmbiguousRuleOperator,
    setContainsAmbiguousRuleOperator,
    thresholdResults: sourceActionThresholdResults,
    containerType,
    selectedEvidenceThresholdId,
    isMatrixModelFocused,
    setIsMatrixModelFocused,
    ActionHoverTooltip,
    ...caretControl,
  }), [
    ruleEditorPanelBoxContainerWidth,
    ruleItemRefs,
    updateRuleItemRefs,
    semanticLineBreakData,
    sourceAction,
    isPreviewAction,
    caretControl,
    mlaUsedInRules,
    showElseClause,
    ruleNumberConfig,
    isDragItemHoveringOnDropZone,
    setIsDragItemHoveringOnDropZone,
    isDragging,
    setIsDragging,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    containsAmbiguousRuleOperator,
    setContainsAmbiguousRuleOperator,
    simpleEditorMode,
    containerType,
    selectedEvidenceThresholdId,
    sourceActionThresholdResults,
    isMatrixModelFocused,
    setIsMatrixModelFocused,
    ActionHoverTooltip,
  ]);

  const mlaEditorPanelBoxContainerRef = useRef(null);
  const [mlaContextSourceData, setMlaContextSourceData] = useState<MLASourceData>({
    type: undefined,
    data: undefined,
    attributeTokenMap: undefined,
  });

  const mlaCaretControl = useCaretControl({
    mlaData: mlaContextSourceData?.attributeTokenMap?.components,
    caretType: CaretIndexType.MLA,
    sourceId: mlaContextSourceData?.attributeTokenMap?.attribute.attribute_id,
    ruleEditorPanelBoxContainerRef: mlaEditorPanelBoxContainerRef,
    isMatrixModelFocused,
  });

  useEffect(() => {
    if (!isolatedEditingBlock.originType && mlaContextSourceData.type) {
      setMlaContextSourceData({
        type: undefined,
        data: undefined,
        attributeTokenMap: undefined,
      });
    }
  }, [mlaContextSourceData, isolatedEditingBlock]);

  const mlaContext = useMemo(() => ({
    sourceData: mlaContextSourceData,
    setSourceData: setMlaContextSourceData,
    simpleEditorMode,
    mlaEditorPanelBoxContainerRef,
    handleCloseMLAEditor: () => {},
    ...mlaCaretControl,
  }), [mlaContextSourceData, simpleEditorMode, mlaCaretControl]);

  useEffect(() => {
    if (!ruleEditorPanelBoxContainerRef.current) return undefined;
    const resizeObserver = new ResizeObserver(() => {
      if (!ruleEditorPanelBoxContainerRef?.current) return false;

      const { width } = ruleEditorPanelBoxContainerRef.current.getBoundingClientRect();
      setRuleEditorPanelBoxContainerWidth(width);
      return width;
    });

    resizeObserver.observe(ruleEditorPanelBoxContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const onEscapeKeyDown = useCallback(() => {
    if (!previewActionBeingBuilt) {
      if (isolatedEditingBlock.originType) {
        setIsolatedEditingBlock({
          originPublicId: undefined,
          originType: undefined,
          originCaretIndex: undefined,
        });
      } else {
        dispatch(resetNodeAndConnectorState());
      }
    }
  }, [dispatch, isolatedEditingBlock, previewActionBeingBuilt]);

  useKeyboardKeyEvent('Escape', onEscapeKeyDown);

  // Reset initialization state when sourceAction changes
  useEffect(() => {
    setIsInitializing(true);
  }, [sourceAction?.publicId]);

  // Clear initialization state once sourceAction is available and stable
  useEffect(() => {
    if (sourceAction && isInitializing) {
      // Use a small timeout to ensure any Redux state updates have settled
      const timeoutId = setTimeout(() => {
        setIsInitializing(false);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [sourceAction, isInitializing]);

  const rules = useMemo<JSX.Element | null>(() => (
    sourceAction && (!isEmpty(sourceAction.rules) || isPreviewAction)
      ? <RuleLinesContainer editable={editable} />
      : null
  ), [editable, isPreviewAction, sourceAction]);

  const actionString = useMemo<JSX.Element | null>(() => (
    sourceAction
    && sourceAction.type !== ActionType.OUTCOME
    && <FocusedActionText focusedAction={sourceAction} hasRules={Boolean(rules)} />
  ) || null, [sourceAction, rules]);

  const isDrawerOrIsland = [ContainerType.DRAWER, ContainerType.ISLAND].includes(containerType);

  const isDrawerOrModal = isDrawerOrIsland
    || (
      containerType === ContainerType.MODAL
      && featureFlags?.ruleExplainabilityPhase > 0
    );

  const relevantEngineAssistanceData = useMemo(
    () => findLastOperation({
      operations: operationDescriptions,
      publicId: sourceAction.publicId,
      types: [OperationMessageType.actionRuleThreshold, OperationMessageType.undo],
    }), [operationDescriptions, sourceAction]);

  useEffect(() => {
    const ambiguousRuleOperatorUsed = checkIfRuleHasAmbiguousKey(cloneDeep(sourceAction.rules));
    setContainsAmbiguousRuleOperator(ambiguousRuleOperatorUsed);

    dispatch(setConnectorCanCreateItemStatus(!ambiguousRuleOperatorUsed));
    if (ambiguousRuleOperatorUsed) {
      dispatchAssistanceToolbar({
        id: uuidv4(),
        publicId: sourceAction.publicId,
        type: OperationMessageType.actionRuleThreshold,
        timestamp: Date.now(),
        message: WorkflowOperationType.AMBIGUOUS_RULE_FOUND,
        data: undefined,
      });
    }
  }, [dispatch, dispatchAssistanceToolbar, sourceAction.publicId, sourceAction.rules]);

  // there are performance issues if we don't debounce this handler
  const debouncedDragLeaveHandler = debounce(
    () => setIsDragItemHoveringOnDropZone(undefined), 350);

  const ruleLineVisible = !simpleEditorMode && Boolean(ruleNumberConfig) && Boolean(rules);

  if (!sourceAction) return null;

  if (featureFlags.ruleBuilderRefactor) {
    return (
      <RuleBuilderContainer
        sourceAction={sourceAction}
        containerType={containerType}
        thresholdResults={sourceActionThresholdResults}
        isPreviewOutcome={isPreviewAction}
        ActionHoverTooltip={ActionHoverTooltip}
      />
    );
  }

  return (
    <RuleEditorContext.Provider value={context}>
      <MLAEditorContext.Provider value={mlaContext}>
        {/* @ts-expect-error - TODO: fix this */}
        <DndProvider backend={HTML5Backend} context={window}>
          <StyledThresholdTabContainer
            data-dd-privacy="allow"
            ref={ruleEditorPanelBoxContainerRef}
            lineNumberBarVisible={ruleLineVisible}
          >
            {importedAttributeDetails}
            <StyledRuleThresholdEditor
              onDragLeave={debouncedDragLeaveHandler}
              isDragging={isDragging}
            >
              {tagDescription}
              <StyledRuleLineContentWrapper>
                {(rules || isPreviewAction) && (
                  <StyledRuleThresholdContainer
                    containerType={containerType}
                    simpleEditorMode={simpleEditorMode}
                  >
                    <StyledRuleThresholdContentArea containerType={containerType}>
                      {rules}
                    </StyledRuleThresholdContentArea>
                  </StyledRuleThresholdContainer>
                )}
              </StyledRuleLineContentWrapper>
            </StyledRuleThresholdEditor>
            {isDrawerOrModal && (
              <StyledRuleThresholdBottomArea
                hasRules={Boolean(rules)}
                containerType={containerType}
                simpleEditorMode={simpleEditorMode}
              >
                {relevantEngineAssistanceData && isDrawerOrIsland && inEditMode && (
                  <AssistanceToolbar
                    assistanceMessageObject={relevantEngineAssistanceData}
                    scrollableViewMode={Boolean(focusedActionMatrixModel)}
                  />
                )}
                {!simpleEditorMode && (
                  <>
                    {actionString && (
                      <StyledPre containerType={containerType}>
                        {actionString}
                      </StyledPre>
                    )}
                    {focusedActionMatrixModel}
                  </>
                )}
              </StyledRuleThresholdBottomArea>
            )}
            {rules && isDrawerOrModal && <MLAFootnotesContainer />}
            {(
              // should only render if all of the required EvidencesTable props are present
              entityToken && evaluationToken && thresholdId && legacyActionId
              // checking ContainerType prevents the EvidencesTable from rendering in unintended views
            ) && containerType === ContainerType.DRAWER && (
              <EvidencesTable
                thresholdId={thresholdId}
                actionId={legacyActionId}
                entityToken={entityToken}
                evaluationToken={evaluationToken}
                isPortfolioEvaluation={sourceActionThresholdResults?.is_portfolio_evaluation}
                dimensions={dimensions}
              />
            )}
          </StyledThresholdTabContainer>
        </DndProvider>
      </MLAEditorContext.Provider>
    </RuleEditorContext.Provider>
  );
}
